import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { getCriticalTimeoutMs } from "utils/request-timeouts";

import { emptySplitApi } from "./empty-split-api";
import type {
  AmbientScribePurchaseSummary,
  AmbientScribeTrialActivation,
  AmbientScribeTrialOffer,
  PaywallFeature,
  PaywallFeatureSubscription,
  PaywallFeatureSubscriptionCreateResponse,
  PaywallFeatureSubscriptionFilter,
  PaywallFeatureSubscriptionInput,
  PaywallFeaturesStatus,
  PaywallLicenseFilter
} from "./types/paywall.type";

const DEFAULT_PAYWALL_LICENSE_QUERY =
  "{id,user{id},expires_at,subscription{practice_id}}";

const paywallApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPaywallFeatureByKey: builder.query<PaywallFeature, string>({
      query: (featureKey) => `/paywall/features/?feature_key=${featureKey}`,
      providesTags: ["PaywallFeatures"]
    }),
    getPaywallFeature: builder.query<PaywallFeature, number>({
      query: (id) => `/paywall/features/${id}/`,
      providesTags: ["PaywallFeatures"]
    }),
    getPaywallLicenses: builder.query<
      PaywallFeature[],
      Partial<PaywallLicenseFilter>
    >({
      query: (filter) =>
        `/paywall/features/${formatValidQueryParamUrlCommaSeparated(filter, true)}&query=${DEFAULT_PAYWALL_LICENSE_QUERY}`,
      providesTags: ["PaywallLicenses"]
    }),
    getCurrentUserPaywallFeatures: builder.query<PaywallFeaturesStatus, void>({
      // Critical bootstrap sibling — armed with the LD bootstrap timeout
      // (undefined = off = no timeout). TIMEOUT_ERROR retries via the wrapper.
      query: () => ({
        url: `auth/users/me/features/`,
        timeout: getCriticalTimeoutMs()
      }),
      providesTags: ["PaywallUserFeatures"]
    }),
    getFeatureSubscriptions: builder.query<
      PaywallFeatureSubscription[],
      Partial<PaywallFeatureSubscriptionFilter>
    >({
      query: (filter) =>
        `/paywall/feature-subscriptions/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["PaywallFeatureSubscriptions"]
    }),
    // A Stripe checkout, or (SN-19868) the trial without a card when the practice has no usable
    // Stripe customer: then it answers 200 with the trial activation body.
    createFeatureSubscription: builder.mutation<
      PaywallFeatureSubscriptionCreateResponse | AmbientScribeTrialActivation,
      PaywallFeatureSubscriptionInput
    >({
      query: (body) => ({
        url: `/paywall/feature-subscriptions/`,
        method: "POST",
        body
      }),
      // Not `PaywallUserFeatures`, for the reason given on `activateAmbientScribeTrial`: a started
      // trial is read by the trial hook's own features refetch, which this would race. A checkout
      // leaves the page, so it loses nothing.
      invalidatesTags: ["PaywallFeatureSubscriptions", "PaywallLicenses"]
    }),
    // SN-19868: the in-app trial with no card. Only asked while AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE is on.
    getAmbientScribeTrialOffer: builder.query<AmbientScribeTrialOffer, void>({
      query: () => `/ai/ambient-scribes/trial-offer/`,
      providesTags: ["AmbientScribeTrialOffer"]
    }),
    activateAmbientScribeTrial: builder.mutation<
      AmbientScribeTrialActivation,
      void
    >({
      query: () => ({
        url: `/ai/ambient-scribes/activate-subscription/`,
        method: "POST",
        body: { is_trial: true }
      }),
      // Deliberately not `PaywallUserFeatures`: the trial hook refetches it and waits for the
      // answer. An invalidation refetch is not one RTK tracks, so a lazy trigger issued while it
      // is in flight resolves at once with the stale cached value — measured: both the hook and
      // the host's re-check read "not available" and the screen never swapped. Nor
      // `AmbientScribeTrialOffer`: refreshed in place it answers "no offer" (the user now holds
      // a seat) and repaints the screen being replaced as the card checkout. It is re-asked on
      // every mount anyway.
      invalidatesTags: ["PaywallFeatureSubscriptions", "PaywallLicenses"]
    }),
    // Who tried Scribe, the practice's price and whether checkout would refuse, read before the
    // licence count is chosen. Only asked while AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE is on. The
    // timeout keeps a slow answer from holding the purchase: the dialog falls back to one licence
    // and says so itself, so the global timeout toast is off (the screen prefetches this unasked).
    getAmbientScribePurchaseSummary: builder.query<
      AmbientScribePurchaseSummary,
      void
    >({
      query: () => ({
        url: `/ai/ambient-scribes/purchase-summary/`,
        timeout: 8000
      }),
      // No timeout retries: a GET otherwise retries twice, and the dialog's loading line would
      // stay up for three timeouts. A failed read still sells one licence.
      extraOptions: { suppressGlobalErrorToast: true, maxRetries: 0 },
      providesTags: ["AmbientScribePurchaseSummary"]
    })
  })
});

export const {
  useGetPaywallFeatureByKeyQuery,
  useGetCurrentUserPaywallFeaturesQuery,
  useLazyGetCurrentUserPaywallFeaturesQuery,
  useCreateFeatureSubscriptionMutation,
  useGetFeatureSubscriptionsQuery,
  useGetAmbientScribeTrialOfferQuery,
  useActivateAmbientScribeTrialMutation,
  useGetAmbientScribePurchaseSummaryQuery
} = paywallApi;
